<script setup lang="ts">
import { screens as defaultBreakpoints } from "tailwindcss/defaultTheme"

import { onMounted, ref, computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useQuery } from "@tanstack/vue-query"
import { ChevronRight, Search, BookOpen } from "lucide-vue-next"

import { cn } from "@/lib/utils"
import { getLocalizedAbstract, listAuthors } from "@/lib/article.ts"

import { type Publication, getPublications, getPublicationArticles } from "@/models/publication"

import Separator from "@/components/ui/separator/Separator.vue"
import Input from "@/components/ui/input/Input.vue"
import ListBox from "@/components/ui-custom/list-box/index.vue"

import DetailContainer from "@/views/layouts/components/DetailContainer.vue"

const { t } = useI18n()
const searchValue = ref("")

// publications
const { data: publications } = useQuery({
  queryKey: ["publications"],
  queryFn: getPublications,
  staleTime: 10 * 60 * 1000 // 10 minutes
})

const selectedPublication = ref<Publication | undefined>()
const selectedPublicationId = computed(() => selectedPublication.value?.id)

const filteredPublications = computed(() => {
  if (!searchValue.value) return publications.value
  return publications.value?.filter((publication) =>
    publicationName(publication).toLowerCase().includes(searchValue.value.toLowerCase())
  )
})

const publicationName = (publication: Publication) => {
  return `
    ${ t("commons.year") } ${ publication.year } -
    ${ t("commons.number") } ${ publication.number }
  `
}

const selectPublication = (publication: Publication) => {
  selectedPublication.value = publication

  goToSlide(1)
}

watch(filteredPublications, (newValue) => {
  selectedPublication.value = newValue?.length ? newValue[0] : undefined
})
// end publications

const { data: articles } = useQuery({
  queryKey: ["publications", selectedPublicationId, "articles"],
  queryFn: ({ signal }) => getPublicationArticles({ signal: signal, id: selectedPublicationId.value?.toString() }),
  enabled: computed(() => !!selectedPublicationId.value),
  staleTime: 10 * 60 * 1000 // 10 minutes
})

const firstSlidingPanel = ref<HTMLDivElement | null>(null)

const goToSlide = (number: number) => {
  if (window.innerWidth >= parseInt(defaultBreakpoints.md)) {
    return
  }

  if (!firstSlidingPanel.value) return

  firstSlidingPanel.value.style.marginLeft = `${number * -100}%`
}

const resetSelected = () => {
  if (filteredPublications.value?.length && selectedPublication) {
    selectedPublication.value = filteredPublications.value[0]
    searchValue.value = ''
  }

  goToSlide(0)
}

onMounted(() => {
  if (filteredPublications.value?.length) selectedPublication.value = filteredPublications.value[0]
})

</script>

<template>
  <div id="navbar" class="container border-white border-t-10 border-b-10 text-primary text-sm">
    <span class="px-2">
      <book-open class="cursor-pointer inline" @click="resetSelected" />  <chevron-right class="inline" />
      <span v-if="selectedPublication">{{ publicationName(selectedPublication) }}</span>
    </span>
  </div>

  <detail-container class="container flex flex-col">
    <div class="flex h-[800px] flex-1 overflow-hidden">
      <div class="sliding-panel md:!ml-0" ref="firstSlidingPanel">
        <div class="flex-0 flex p-2 text-primary">{{ $t("publications.select") }}</div>

        <div class="flex flex-1 flex-col overflow-y-auto border border-r-0 bg-muted p-1">
          <div class="flex-0 flex p-1">
            <form class="relative flex flex-1">
              <Search class="absolute left-2 top-2.5 size-4 text-muted-foreground" />

              <Input v-model="searchValue" :placeholder="`${$t('commons.search')}...`" class="bg-white pl-8" />
            </form>
          </div>

          <div class="flex flex-1 flex-col gap-1 overflow-y-auto bg-muted p-1" v-if="publications">
            <div
              v-for="publication in filteredPublications"
              :key="publication.id"
              :class="
                cn([
                  'flex cursor-pointer border bg-white p-2 hover:bg-primary hover:text-white hover:transition-all hover:duration-300',
                  selectedPublication === publication ? 'bg-primary text-white transition-all duration-300' : ''
                ])
              "
              @click="selectPublication(publication)">
              <div class="flex-1">
                {{ publicationName(publication) }}
              </div>

              <div class="flex min-w-10 justify-center bg-muted px-2 text-foreground">
                {{ publication.articles_count }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="sliding-panel">
        <div class="flex-0 flex p-2 text-primary">{{ $t("publications.selected") }}:</div>

        <div class="flex flex-1 flex-col gap-2 overflow-y-auto border border-l-0 bg-muted p-1">
          <template v-if="selectedPublication">

            <router-link
              :to="{ name: 'show_publication_path', params: { id: selectedPublication.id } }"
              class="flex-0 flex h-[150px] p-1">

              <span class="flex min-h-32 flex-1 gap-4 border bg-white p-2 transition-all duration-500 hover:bg-gray-200">
                <img
                  class="hidden w-[100px] items-center justify-center bg-muted text-primary md:flex"
                  :src="`/publications/${selectedPublication.id}/cover`"
                  alt="COPERTINA" />

                <span class="flex flex-1 flex-col">
                  <span class="font-semibold text-primary">{{ publicationName(selectedPublication) }}</span>

                  <Separator />

                  <span class="flex flex-1 flex-col justify-center">
                    <span class="flex-0 flex">
                      <span class="mr-4 w-[50px] text-primary">{{ $t("publications.issn") }}:</span>
                      <span>{{ selectedPublication.issn }}</span>
                    </span>

                    <span class="flex-0 flex">
                      <span class="mr-4 w-[50px] text-primary">{{ $t("publications.pages") }}:</span>
                      <span>{{ selectedPublication.total_pages }}</span>
                    </span>

                    <span class="flex-0 flex">
                      <span class="mr-4 w-[50px] text-primary">
                        {{ $t("publications.articles") }}:
                      </span>

                      <span>{{ selectedPublication.articles_count }}</span>
                    </span>
                  </span>
                </span>

                <span
                  class="flex-0 flex items-center bg-muted text-primary">
                  <ChevronRight />
                </span>
              </span>

            </router-link>

            <Separator />

            <div class="flex flex-1 flex-col gap-1 overflow-y-auto bg-muted p-1" v-if="articles">
              <list-box
                :url="{ name: 'show_article_path', params: { id: article.id } }"
                :key="`article-${article.id}`"
                v-for="article in articles"
              >
                <template #title>
                  {{ article.title }}
                </template>

                <template #default>
                  <div class="flex-0 text-gray-600 py-1" v-if="article?.authors?.length">
                    {{ $t("commons.by") }} <span class="font-semibold">{{ listAuthors(article.authors) }}</span>
                  </div>

                  <div class="line-clamp-2 flex-1" v-if="getLocalizedAbstract(article)">{{ getLocalizedAbstract(article) }}</div>
                </template>
              </list-box>

            </div>
          </template>
        </div>
      </div>
    </div>
  </detail-container>
</template>
